import { useQuery } from "@tanstack/react-query";
import { get, patch, post } from "./divertidogsApiUtils";
import { normalizarFecha } from "../utils/dateUtils";
import { TipoServicio } from "./ventasServicios";

// Asistencia Diaria

export interface AsistenciaDiaria {
  id: string;
  fechaGeneracion: Date;
  asistencias: ItemListadoAsistenciaDiaria[];
}

export interface ItemListadoAsistenciaDiaria {
  id: string;
  idMascota: string;
  nombreMascota: string;
  raza: string;
  nombrePropietario: string;
  idReservaGuarderia?: string;
  idReservaHotel?: string;
  asistio: boolean;
}

export const obtenerAsistenciaDiaria =
  async (): Promise<AsistenciaDiaria | null> => {
    const resp: AsistenciaDiaria | undefined = await get<
      AsistenciaDiaria | undefined
    >("listas_asistencia/hoy");
    if (resp) {
      return resp;
    } else {
      return null;
    }
  };

export const QUERY_OBTENER_ASISTENCIA_DIARIA =
  "QUERY_OBTENER_ASISTENCIA_DIARIA";

export const useObtenerAsistenciaDiaria = () => {
  return useQuery(
    [QUERY_OBTENER_ASISTENCIA_DIARIA],
    async () => await obtenerAsistenciaDiaria()
  );
};

export const generarAsistenciaDiaria = async () => {
  await post<void, void>("listas_asistencia/hoy", undefined);
};

// Check in

export const checkIn = async ({
  idListaAsistencia,
  asistencias,
}: {
  idListaAsistencia: string;
  asistencias: Map<string, boolean>;
}) => {
  await patch<Object, void>(
    "listas_asistencia/" + idListaAsistencia,
    Object.fromEntries(asistencias)
  );
};

// Asistencias proyectadas

export interface IAsistenciaProyectada {
  idMascota: string;
  nombreMascota: string;
  nombrePropietario: string;
  idReservaGuarderia?: string;
  idReservaHotel?: string;
}

export type AsistenciaProyectada = AsistenciaFutura | AsistenciaPasada;

export interface AsistenciaFutura extends IAsistenciaProyectada {}

export interface AsistenciaPasada extends IAsistenciaProyectada {
  asistio: boolean;
}

export type NivelListaAsistenciaProyectada = "BAJO" | "MEDIO" | "ALTO";

export interface IListaAsistenciaProyectada {
  fecha: string;
  conteoMascotas: number;
  tipo: "PASADA" | "FUTURA";
  nivel: NivelListaAsistenciaProyectada;
  asistencias: AsistenciaProyectada[];
}

export interface ListaAsistenciaFutura extends IListaAsistenciaProyectada {
  tipo: "FUTURA";
  asistencias: AsistenciaFutura[];
}

export interface ListaAsistenciaPasada extends IListaAsistenciaProyectada {
  tipo: "PASADA";
  idLista: string;
  asistencias: AsistenciaPasada[];
}

export type ListaAsistenciaProyectada =
  | ListaAsistenciaFutura
  | ListaAsistenciaPasada;

export function esAsistenciaPasada(
  object: AsistenciaProyectada
): object is AsistenciaPasada {
  return "asistio" in object;
}

export const obtenerListasAsistenciasProyectadas = async (): Promise<
  Array<ListaAsistenciaProyectada>
> => {
  let listas =
    (await get<Array<ListaAsistenciaProyectada>>(
      "listas_asistencia/asistencias_proyectadas"
    )) || [];

  return listas;
};

export const QUERY_OBTENER_LISTAS_ASISTENCIAS_PROYECTADAS =
  "QUERY_OBTENER_LISTAS_ASISTENCIAS_PROYECTADAS";

export const useObtenerListasAsistenciasProyectadas = () => {
  return useQuery(
    [QUERY_OBTENER_LISTAS_ASISTENCIAS_PROYECTADAS],
    async () => await obtenerListasAsistenciasProyectadas()
  );
};

export const obtenerListaAsistencia = async (
  id: string
): Promise<AsistenciaDiaria | null> => {
  const resp: AsistenciaDiaria | undefined = await get<
    AsistenciaDiaria | undefined
  >(`listas_asistencia/${id}`);
  if (resp) {
    return resp;
  } else {
    return null;
  }
};

// Obtener asistencia

export const QUERY_OBTENER_ASISTENCIA = "QUERY_OBTENER_ASISTENCIA";

export const useObtenerListaAsistencia = (id: string) => {
  return useQuery(
    [QUERY_OBTENER_ASISTENCIA],
    async () => await obtenerListaAsistencia(id)
  );
};

// Existe asistencia

export const existeListaAsistenciaDeHoy = async () => {
  return get<boolean>(`listas_asistencia/hoy/existe`);
};

export const QUERY_EXISTE_LISTA_ASISTENCIA_DE_HOY =
  "QUERY_EXISTE_LISTA_ASISTENCIA_DE_HOY";

export const useExisteListaAsistenciaDeHoy = () => {
  return useQuery(
    [QUERY_EXISTE_LISTA_ASISTENCIA_DE_HOY],
    async () => await existeListaAsistenciaDeHoy()
  );
};

// Fechas proyectadas
export const QUERY_FECHAS_PROYECTADAS = "QUERY_FECHAS_PROYECTADAS";

export const useObtenerFechasProyectadas = (idMascota: string) => {
  return useQuery(
    [QUERY_FECHAS_PROYECTADAS],
    async () => await obtenerFechasProyectadas(idMascota)
  );
};

export const obtenerFechasProyectadas = async (idMascota: string) => {
  const res = await get<Date[]>(
    `listas_asistencia/fechas_proyectadas?idMascota=${idMascota}`
  );
  if (res === undefined) {
    throw new Error("No se pudo obtener las fechas proyectadas");
  }
  return res.map((fecha) => normalizarFecha(fecha));
};

// Promedio Asistencia del mes
export const QUERY_PROMEDIO_ASISTENCIA_DEL_MES =
  "QUERY_PROMEDIO_ASISTENCIA_DEL_MES";

export const useObtenerPromedioAsistenciaDelMes = () =>
  useQuery(
    [QUERY_PROMEDIO_ASISTENCIA_DEL_MES],
    async () => await obtenerPromedioAsistenciaDelMes()
  );

export const obtenerPromedioAsistenciaDelMes = async () => {
  return get<number>(`asistencias/promedio_del_mes`);
};

// Asistencias de mascota en rango de fecha
export type AsistenciaEnRango = {
  id: string;
  idMascota: string;
  fecha: string;
  asistio: boolean;
  tipoServicio: TipoServicio;
};

export const QUERY_ASISTENCIAS_EN_RANGO = "QUERY_ASISTENCIAS_EN_RANGO";

export const useListarAsistenciasEnRango = (
  inicio: string,
  fin: string,
  idMascota: string
) =>
  useQuery(
    [QUERY_ASISTENCIAS_EN_RANGO, inicio, fin, idMascota],
    async () => await listarAsistenciasEnRango(inicio, fin, idMascota)
  );

export const listarAsistenciasEnRango = async (
  inicio: string,
  fin: string,
  idMascota: string
) => {
  return get<Array<AsistenciaEnRango>>(
    `asistencias?inicio=${inicio}&fin=${fin}&idMascota=${idMascota}`
  );
};

// Detalle de asistencia
export type AsistenciaDetalle = {
  id: string;
  idMascota: string;
  asistio: boolean;
  idReserva: string;
  tipoServicio: TipoServicio;
  fecha: string;
};

export const QUERY_OBTENER_ASISTENCIA_POR_ID =
  "QUERY_OBTENER_ASISTENCIA_POR_ID";

export const obtenerAsistenciaPorId = async (id: string) => {
  return get<AsistenciaDetalle>(`asistencias/${id}`);
};

export const useObtenerAsistenciaPorId = (id: string, enabled: boolean) =>
  useQuery({
    queryKey: [QUERY_OBTENER_ASISTENCIA_POR_ID, id],
    queryFn: async () => await obtenerAsistenciaPorId(id),
    enabled,
  });
